if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');
      }

      get variantIdSelect() {
        return this.querySelector('[name="id"]');
      }

      getOptionControlsOrdered() {
        if (!this.mainVariantSelects) return [];
        return Array.from(this.mainVariantSelects.querySelectorAll('[data-option-index]')).sort(
          (a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex)
        );
      }

      setMainFormFromStickyVariant() {
        if (!this.currentVariant || !this.mainVariantSelects) return;
        const controls = this.getOptionControlsOrdered();
        let lastControlSet = null;
        controls.forEach((control, index) => {
          const variantOptionVal = this.currentVariant.options[index];
          if (variantOptionVal == null) return;
          if (control.tagName === 'SELECT') {
            const options = control.querySelectorAll('option');
            options.forEach((opt) => { opt.disabled = false; });
            control.value = variantOptionVal;
            options.forEach((opt) => opt.removeAttribute('selected'));
            const selectedOpt = control.querySelector(`option[value="${CSS.escape(variantOptionVal)}"]`);
            if (selectedOpt) selectedOpt.setAttribute('selected', 'selected');
            lastControlSet = control;
          } else {
            const input = control.querySelector(`input[value="${CSS.escape(variantOptionVal)}"]`);
            if (input && input.type === 'radio') {
              input.checked = true;
              lastControlSet = input;
            }
          }
        });
        if (lastControlSet) {
          lastControlSet.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      connectedCallback() {
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');
        this.container = this.closest('.sticky-atc-bar');
        this.dynamicUnavailableOptions = [];

        this.variantData = this.getVariantData();
        this.select = this.querySelector('select[data-sticky-variant-select]') || this.querySelector('select');

        this.init();
        this.select.addEventListener('change', () => {
          if (this.isUpdating) return;
          this.isUpdating = true;

          this.mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');
          const selectedVal = this.variantIdSelect.value;
          const selectedVariantId = selectedVal === '' ? null : Number(selectedVal);
          this.currentVariant = selectedVariantId != null
            ? this.variantData.find((v) => v.id === selectedVariantId)
            : null;

          if (this.mainVariantSelects && this.currentVariant) {
            this.setMainFormFromStickyVariant();
          }
          if (this.currentVariant) {
            this.removePlaceholderOption();
            this.updatePrice();
          }

          setTimeout(() => {
            this.isUpdating = false;
          }, 0);

          this.updateButton(true, '', false);
          if (!this.currentVariant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!this.currentVariant.available, FoxTheme.variantStrings.soldOut);
          }
        });
      }

      getVariantData() {
        this.variantData =
          this.variantData || JSON.parse(this.container.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }

      init() {
        if (!this.productFormActions) {
          this.container.classList.add('sticky-atc-bar--show');
          return;
        }
        this.productId = this.dataset.productId;

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.checkDevice.bind(this);
        this.checkDevice();

        const rootMargin = `${this.productFormActions.offsetHeight}px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.container.classList.toggle('sticky-atc-bar--show', !entry.isIntersecting);
            });
          },
          { threshold: 1, rootMargin }
        );
        this.observer.observe(this.productFormActions);
        this.syncWithMainProductForm();
        this.syncStickyFromMainForm();
      }

      syncStickyFromMainForm() {
        if (!this.mainProductInfo || !this.select) return;
        const form = this.mainProductInfo.querySelector('form[is="product-form"]');
        const idInput = form?.querySelector('input[name="id"]');
        const rawId = idInput?.value?.trim();
        const variantId = rawId ? Number(rawId) : null;
        const hasPlaceholder = this.select.querySelector('option[data-option-placeholder="true"]');
        if (!variantId || !this.variantData.some((v) => v.id === variantId)) {
          if (hasPlaceholder) {
            this.currentVariant = null;
            this.select.value = '';
            const stickyIdInput = this.querySelector('[name="id"]');
            if (stickyIdInput) stickyIdInput.value = '';
            this.updateButton(true, '', true);
          }
          return;
        }
        this.currentVariant = this.variantData.find((v) => v.id === variantId);
        this.select.value = variantId;
        const stickyIdInput = this.querySelector('[name="id"]');
        if (stickyIdInput) stickyIdInput.value = variantId;
        this.removePlaceholderOption();
        this.updatePrice();
        this.updateButton(!this.currentVariant.available, FoxTheme.variantStrings.soldOut);
      }

      checkDevice() {
        document.documentElement.style.setProperty('--sticky-atc-bar-height', this.clientHeight + 'px');
      }

      updateButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector('.sticky-atc-bar__form');
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }
      }

      updatePrice() {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = this.currentVariant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!this.currentVariant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            this.currentVariant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${this._getBaseUnit()}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      ensureStickySelectHasVariant(variant) {
        if (!variant || !this.select) return;
        const hasOption = Array.from(this.select.options).some((opt) => Number(opt.value) === variant.id);
        if (hasOption) return;
        this.dynamicUnavailableOptions.forEach((opt) => opt.remove());
        this.dynamicUnavailableOptions = [];
        const option = document.createElement('option');
        option.value = variant.id;
        option.setAttribute('data-dynamic-unavailable', 'true');
        option.disabled = true;
        option.textContent = `${variant.title} — ${FoxTheme.variantStrings?.soldOut || 'Sold out'}`;
        this.select.appendChild(option);
        this.dynamicUnavailableOptions.push(option);
      }

      removeDynamicUnavailableOptions() {
        this.dynamicUnavailableOptions.forEach((opt) => opt.remove());
        this.dynamicUnavailableOptions = [];
      }

      removePlaceholderOption() {
        const placeholder = this.select?.querySelector('option[data-option-placeholder="true"]');
        if (placeholder) placeholder.remove();
      }

      syncWithMainProductForm() {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          if (!this.mainProductInfo || event.data.sectionId !== this.mainProductInfo.dataset.section) return;
          const variant = event.data.variant;
          const variantInput = this.querySelector('[name="id"]');
          if (!variantInput) return;

          this.currentVariant = variant;
          if (variant) {
            this.removePlaceholderOption();
            if (variant.available) {
              this.removeDynamicUnavailableOptions();
            } else {
              this.ensureStickySelectHasVariant(variant);
            }
            variantInput.value = variant.id;
            if (this.select) this.select.value = variant.id;
            this.updatePrice();
          } else if (this.select && this.select.querySelector('option[data-option-placeholder="true"]')) {
            variantInput.value = '';
            this.select.value = '';
          }
          this.updateButton(true, '', false);
          if (!variant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
          }
        });
      }
    }
  );
}
